import { useQuery } from '@tanstack/react-query'
import { deviceTodoQuery } from '../api/changes'
import { ApiError } from '../api/client'
import { Forbidden } from '../ui/Forbidden'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila "AI Actions" a fisei masinii - `Changes::deviceTodo`,
 * `views/version-3/changes/device_todo.php`.
 *
 * Memo pe echipament: ce a pregatit agentul AI pentru masina asta - din
 * propunerile vii ale firmei care o ating. Prima pe lista unei propuneri de
 * change e canary-ul, si se spune. Acelasi drept ca terminalul: cine poate
 * trimite o comanda vede ce s-a pregatit.
 */

const CHIPS: Record<string, [string, string, string]> = {
  proposed: ['#8a6116', '#fdf3dd', 'Waiting for approval'],
  approved: ['#1f5f8b', '#e3f0fa', 'Approved'],
  running: ['#1f5f8b', '#e3f0fa', 'Running'],
}

export function DeviceAiTodo({ id }: ScreenProps) {
  const { data, error } = useQuery(deviceTodoQuery(id))

  if (error instanceof ApiError && error.status === 403) return <Forbidden />
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return (
    <div className="row">
      <div className="col-md-12">
        {data.drafts.length === 0 && (
          <div className="panel panel-default">
            <div className="panel-body text-muted">
              Nothing queued for this machine. When the agent proposes a change that touches it, it shows up here - and
              under <a href="/app/change">Change Proposals</a>.
            </div>
          </div>
        )}

        {data.drafts.map((draft) => {
          const chip = CHIPS[draft.status] ?? CHIPS.proposed
          return (
            <div className="panel panel-default" key={draft.ID}>
              <div className="panel-body">
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong>{draft.title}</strong>
                  {draft.mode === 'incident' && (
                    <span style={{ background: '#b42318', color: '#fff', padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                      INCIDENT
                    </span>
                  )}
                  <span style={{ background: chip[1], color: chip[0], padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                    {chip[2]}
                  </span>
                  {draft.is_canary && (
                    <span
                      style={{
                        background: '#fdf3dd',
                        color: '#8a6116',
                        border: '1px solid #8a6116',
                        padding: '2px 9px',
                        borderRadius: 10,
                        fontSize: 11,
                      }}
                    >
                      canary - first machine
                    </span>
                  )}
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    machine {draft.position} of {draft.total_devices}
                  </span>
                </div>

                {draft.command !== '' ? (
                  <pre style={{ margin: '8px 0 0', padding: '6px 10px', background: '#f6f7f9', borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {draft.command_type + '  ' + draft.command}
                  </pre>
                ) : (
                  <div className="text-muted" style={{ marginTop: 6, fontSize: 12 }}>
                    {draft.command_type}
                  </div>
                )}

                <div style={{ marginTop: 8 }}>
                  <a href="/app/change" className="btn btn-default btn-sm">
                    Open in Change Proposals
                  </a>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
